import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CancellationRefund = () => {
  const policyData = [
    {
      id: 1,
      title: "Listing Fees – No Cancellation & No Refund",
      icon: "fas fa-ban",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      content: [
        "Any fees paid for listing clothes or jewellery on the VestrioZ platform are strictly non-cancellable and non-refundable.",
        "Once a listing is created and payment is completed, no refunds will be issued under any circumstances.",
        "This applies even if the item is not rented or sold."
      ]
    },
    {
      id: 2,
      title: "Membership Fees – No Refund Policy",
      icon: "fas fa-crown",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      content: [
        "If you purchase a membership plan, the membership amount is strictly non-refundable.",
        "No refunds will be provided for membership fees, even in case of technical issues or genuine concerns.",
        "Membership benefits are activated immediately upon successful payment and cannot be reversed.",
        "This policy applies to all membership tiers and subscription plans offered on the platform."
      ]
    },
    {
      id: 3,
      title: "Service Usage",
      icon: "fas fa-cogs",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      content: [
        "Listing fees are charged for providing platform services such as item visibility, hosting, moderation, and promotion.",
        "Membership fees provide access to premium features, unlimited listings, and enhanced platform benefits.",
        "These services begin immediately upon successful payment; hence, refunds are not applicable."
      ]
    },
    {
      id: 4,
      title: "Exceptional Cases",
      icon: "fas fa-exclamation-triangle",
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      content: [
        "Refunds may be considered only in the event of a technical error resulting in multiple or duplicate charges for listing fees.",
        "Membership fees are not eligible for refunds under any circumstances, including technical errors.",
        "Such cases for listing fees must be reported within 48 hours of the transaction, along with valid proof."
      ]
    },
    {
      id: 5,
      title: "Refund Processing (If Applicable)",
      icon: "fas fa-clock",
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      content: [
        "Approved refunds (only for exceptional listing fee technical cases) will be processed to the original payment method.",
        "Refund processing time is 7–10 working days, depending on the payment provider.",
        "Membership fees are excluded from any refund processing under all circumstances."
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Cancellation & Refund Policy - VestrioZ</title>
        <meta
          name="description"
          content="Learn about VestrioZ's cancellation and refund policy for rentals and purchases."
        />
        <meta name="keywords" content="cancellation policy, refund policy, VestrioZ" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-cream via-light-beige to-sage-green/20">
        <Navbar />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Header Section */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-dark-charcoal mb-3 sm:mb-4">
              Cancellation & Refund Policy
            </h1>
            <p className="text-base sm:text-lg text-medium-gray max-w-3xl mx-auto leading-relaxed">
              At VestrioZ, we strive to maintain transparency and fairness across our platform.
              Please read our cancellation and refund policy carefully before making any payments.
            </p>
          </div>

          {/* Policy Sections */}
          <div className="space-y-6 sm:space-y-8">
            {policyData.map((section) => (
              <div
                key={section.id}
                className={`${section.bgColor} ${section.borderColor} border-l-4 rounded-r-xl sm:rounded-r-2xl shadow-lg overflow-hidden`}
              >
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 ${section.bgColor} rounded-full flex items-center justify-center flex-shrink-0 border-2 ${section.borderColor}`}>
                      <i className={`${section.icon} ${section.color} text-base sm:text-lg`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-dark-charcoal mb-2">
                        {section.id}. {section.title}
                      </h2>
                    </div>
                  </div>

                  <div className="ml-0 sm:ml-16 space-y-3 sm:space-y-4">
                    {section.content.map((point, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 ${section.color.replace('text-', 'bg-')} rounded-full mt-2 flex-shrink-0`}></div>
                        <p className="text-medium-gray leading-relaxed text-sm sm:text-base">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Information Section */}
          <div className="mt-8 sm:mt-12 bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border border-sage-green/10">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-sage-green/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="fas fa-headset text-sage-green text-xl sm:text-2xl"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-dark-charcoal mb-3 sm:mb-4">
                6. Contact Information
              </h2>
              <p className="text-medium-gray mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                For any payment-related concerns, please contact us at:
              </p>

              <div className="bg-sage-green/5 rounded-xl p-4 sm:p-6 border border-sage-green/20">
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3">
                  <i className="fas fa-envelope text-sage-green text-lg sm:text-xl"></i>
                  <a
                    href="mailto:info.vestrioz@gmail.com"
                    className="text-lg sm:text-xl font-semibold text-sage-green hover:text-dark-charcoal transition-colors duration-200 break-all"
                  >
                    info.vestrioz@gmail.com
                  </a>
                </div>
                <p className="text-xs sm:text-sm text-medium-gray mt-2 sm:mt-3">
                  Our support team will respond within 24-48 working hours
                </p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-6 sm:mt-8 bg-gradient-to-r from-sage-green/10 to-medium-gray/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-sage-green/20">
            <div className="flex items-start space-x-3 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sage-green/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="fas fa-info-circle text-sage-green text-lg sm:text-xl"></i>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-dark-charcoal mb-2 sm:mb-3">
                  Important Notice
                </h3>
                <p className="text-medium-gray leading-relaxed text-sm sm:text-base">
                  By using the VestrioZ platform and making any payments, you acknowledge that you have
                  read, understood, and agree to this Cancellation & Refund Policy. This policy is
                  subject to change, and any updates will be communicated through our platform.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Link
              to="/contact"
              className="bg-sage-green text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold text-sm sm:text-base hover:bg-sage-green/90 transition-all duration-200 transform hover:scale-[1.02] focus:ring-2 focus:ring-sage-green focus:ring-offset-2 text-center"
            >
              <i className="fas fa-envelope mr-2 sm:mr-3"></i>
              Contact Support
            </Link>

            <Link
              to="/"
              className="bg-white text-sage-green border-2 border-sage-green py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold text-sm sm:text-base hover:bg-sage-green hover:text-white transition-all duration-200 transform hover:scale-[1.02] focus:ring-2 focus:ring-sage-green focus:ring-offset-2 text-center"
            >
              <i className="fas fa-home mr-2 sm:mr-3"></i>
              Back to Home
            </Link>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default CancellationRefund;